import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import { useParams, Link } from "react-router-dom";
import moment from "moment";
import { fetchActivityLogs, fetchgastenverbruikone } from "../Reducers/actions";
import { message } from "../../../Reducers/loading/actions";

const ActivityLogPage = ({ logs, loading, error, gastverbruik, fetchActivityLogs, fetchgastenverbruikone, message }) => {
  const { id } = useParams();

  // Filter states
  const [filters, setFilters] = useState({
    startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
    endDate: moment().format('YYYY-MM-DD'),
    actionType: '',
    success: 'all',
    userType: '',
    searchText: ''
  });

  const [filteredLogs, setFilteredLogs] = useState([]);

  useEffect(() => {
    if (id) {
      fetchgastenverbruikone(id);
      fetchActivityLogs(id);
    }
  }, [id, fetchActivityLogs, fetchgastenverbruikone]);

  useEffect(() => {
    if (logs) {
      applyFilters();
    }
  }, [logs, filters]);

  const applyFilters = () => {
    let filtered = [...logs];

    // Date filtering
    if (filters.startDate) {
      filtered = filtered.filter(log =>
        moment(log.created_at).isSameOrAfter(moment(filters.startDate).startOf('day'))
      );
    }
    if (filters.endDate) {
      filtered = filtered.filter(log =>
        moment(log.created_at).isSameOrBefore(moment(filters.endDate).endOf('day'))
      );
    }

    // Action type filtering
    if (filters.actionType) {
      filtered = filtered.filter(log => log.action_type === filters.actionType);
    }

    // Success filtering
    if (filters.success !== 'all') {
      const successValue = filters.success === 'true';
      filtered = filtered.filter(log => log.success === successValue);
    }

    // User type filtering
    if (filters.userType) {
      filtered = filtered.filter(log =>
        log.performed_by?.user_type === filters.userType
      );
    }

    // Search text filtering
    if (filters.searchText) {
      const searchLower = filters.searchText.toLowerCase();
      filtered = filtered.filter(log =>
        log.description.toLowerCase().includes(searchLower) ||
        log.performed_by?.user_name?.toLowerCase().includes(searchLower) ||
        (log.error_message && log.error_message.toLowerCase().includes(searchLower))
      );
    }

    setFilteredLogs(filtered);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleRefresh = () => {
    if (id) {
      fetchActivityLogs(id);
      message(2000, "info", "Activity logs vernieuwd");
    }
  };

  const clearFilters = () => {
    setFilters({
      startDate: moment().subtract(7, 'days').format('YYYY-MM-DD'),
      endDate: moment().format('YYYY-MM-DD'),
      actionType: '',
      success: 'all',
      userType: '',
      searchText: ''
    });
  };

  const exportToCsv = () => {
    if (filteredLogs.length === 0) {
      message(3000, "warning", "Geen data om te exporteren");
      return;
    }

    const headers = ['Datum', 'Tijd', 'Actie', 'Beschrijving', 'Status', 'Gebruiker', 'User Type', 'Foutmelding'];
    const csvData = filteredLogs.map(log => [
      moment(log.created_at).format('DD-MM-YYYY'),
      moment(log.created_at).format('HH:mm:ss'),
      getActionTypeLabel(log.action_type),
      log.description,
      log.success ? 'Succesvol' : 'Mislukt',
      log.performed_by?.user_name || 'Onbekend',
      getUserTypeLabel(log.performed_by?.user_type),
      log.error_message || ''
    ]);

    const csvContent = [headers, ...csvData]
      .map(row => row.map(field => `"${field}"`).join(','))
      .join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', `activity_logs_${moment().format('YYYY-MM-DD')}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const getActionTypeIcon = (actionType) => {
    const icons = {
      'CREATE_BOOKING': 'fas fa-plus-circle text-success',
      'UPDATE_BOOKING': 'fas fa-edit text-primary',
      'DELETE_BOOKING': 'fas fa-trash-alt text-danger',
      'ACTIVATE_RESERVATION': 'fas fa-power-off text-success',
      'FINISH_BOOKING': 'fas fa-check-circle text-success',
      'ADD_KWH': 'fas fa-bolt text-warning',
      'CHANGE_AMPERE': 'fas fa-plug text-info',
      'MOVE_BOOKING': 'fas fa-map-marker-alt text-primary',
      'PROCESS_PAYMENT': 'fas fa-credit-card text-success',
      'ADD_EXTRA_CREDIT': 'fas fa-coins text-warning',
      'SENSOR_COUPLING': 'fas fa-link text-info',
      'METER_READING': 'fas fa-chart-bar text-primary',
      'STATUS_CHANGE': 'fas fa-sync-alt text-info',
      'EMAIL_SENT': 'fas fa-envelope text-primary',
      'WEBHOOK_RECEIVED': 'fas fa-globe text-info',
      'EARLY_ARRIVAL': 'fas fa-clock text-warning'
    };
    return icons[actionType] || 'fas fa-clipboard text-secondary';
  };

  const getActionTypeLabel = (actionType) => {
    const labels = {
      'CREATE_BOOKING': 'Aangemaakt',
      'UPDATE_BOOKING': 'Gewijzigd',
      'DELETE_BOOKING': 'Verwijderd',
      'ACTIVATE_RESERVATION': 'Geactiveerd',
      'FINISH_BOOKING': 'Afgerond',
      'ADD_KWH': 'kWh Toegevoegd',
      'CHANGE_AMPERE': 'Ampere Gewijzigd',
      'MOVE_BOOKING': 'Verplaatst',
      'PROCESS_PAYMENT': 'Betaling',
      'ADD_EXTRA_CREDIT': 'Extra Tegoed',
      'SENSOR_COUPLING': 'Sensor Gekoppeld',
      'METER_READING': 'Meterstand',
      'STATUS_CHANGE': 'Status Gewijzigd',
      'EMAIL_SENT': 'Email Verzonden',
      'WEBHOOK_RECEIVED': 'Webhook',
      'EARLY_ARRIVAL': 'Vroege Aankomst'
    };
    return labels[actionType] || actionType;
  };

  const getUserTypeLabel = (userType) => {
    const labels = {
      'admin': 'Admin',
      'manager': 'Manager',
      'medewerker': 'Medewerker',
      'system': 'Systeem',
      'webhook': 'Webhook',
      'guest': 'Gast'
    };
    return labels[userType] || 'Onbekend';
  };

  const getUniqueActionTypes = () => {
    const types = [...new Set(logs.map(log => log.action_type))];
    return types.sort();
  };

  const getUniqueUserTypes = () => {
    const types = [...new Set(logs.map(log => log.performed_by?.user_type).filter(Boolean))];
    return types.sort();
  };

  if (loading && !logs.length) {
    return (
      <div className="row">
        <div className="col-12">
          <div className="box box-default">
            <div className="box-body">
              <div className="text-center p-4">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Laden...</span>
                </div>
                <p className="mt-2">Activity logs laden...</p>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="row">
      <div className="col-12">
        <div className="box box-default">
          <div className="box-header with-border">
            <h3 className="box-title">
              <i className="fas fa-history" style={{ marginRight: "8px" }}></i>
              Activity Log
              {gastverbruik && gastverbruik.standplaats && (
                <>
                  {" - "}
                  <strong>{gastverbruik.standplaats.naam}</strong>
                  {gastverbruik.gast && (
                    <span className="text-muted"> ({gastverbruik.gast.voornaam} {gastverbruik.gast.achternaam})</span>
                  )}
                </>
              )}
            </h3>
            <div className="box-tools pull-right">
              <Link
                to={`/gastenverbruik/kampeerplaats/${id}`}
                className="btn btn-info btn-sm"
                style={{ marginRight: "5px" }}
                title="Terug naar reservering"
              >
                <i className="fa fa-arrow-left" /> Terug
              </Link>
              <button
                className="btn btn-success btn-sm"
                onClick={exportToCsv}
                style={{ marginRight: "5px" }}
                title="Exporteer naar CSV"
              >
                <i className="fa fa-download" /> Export CSV
              </button>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleRefresh}
                title="Ververs"
              >
                <i className="fa fa-sync-alt" /> Ververs
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="col-12">
        <div className="box box-default">
          <div className="box-header with-border">
            <h3 className="box-title">
              <i className="fas fa-filter" style={{ marginRight: "8px" }}></i>
              Filters
            </h3>
            <div className="box-tools">
              <button className="btn btn-warning btn-sm" onClick={clearFilters}>
                <i className="fa fa-eraser" /> Wis filters
              </button>
            </div>
          </div>
          <div className="box-body">
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="startDate">Van datum:</label>
                  <input
                    type="date"
                    id="startDate"
                    className="form-control"
                    value={filters.startDate}
                    onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="endDate">Tot datum:</label>
                  <input
                    type="date"
                    id="endDate"
                    className="form-control"
                    value={filters.endDate}
                    onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  />
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="actionType">Actie type:</label>
                  <select
                    id="actionType"
                    className="form-control"
                    value={filters.actionType}
                    onChange={(e) => handleFilterChange('actionType', e.target.value)}
                  >
                    <option value="">Alle acties</option>
                    {logs.length > 0 && getUniqueActionTypes().map(type => (
                      <option key={type} value={type}>{getActionTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="success">Status:</label>
                  <select
                    id="success"
                    className="form-control"
                    value={filters.success}
                    onChange={(e) => handleFilterChange('success', e.target.value)}
                  >
                    <option value="all">Alle statussen</option>
                    <option value="true">Succesvol</option>
                    <option value="false">Mislukt</option>
                  </select>
                </div>
              </div>
            </div>
            <div className="row">
              <div className="col-md-3">
                <div className="form-group">
                  <label htmlFor="userType">Gebruiker type:</label>
                  <select
                    id="userType"
                    className="form-control"
                    value={filters.userType}
                    onChange={(e) => handleFilterChange('userType', e.target.value)}
                  >
                    <option value="">Alle gebruikers</option>
                    {logs.length > 0 && getUniqueUserTypes().map(type => (
                      <option key={type} value={type}>{getUserTypeLabel(type)}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="col-md-9">
                <div className="form-group">
                  <label htmlFor="searchText">Zoeken:</label>
                  <input
                    type="text"
                    id="searchText"
                    className="form-control"
                    placeholder="Zoek in beschrijving, gebruikersnaam of foutmeldingen..."
                    value={filters.searchText}
                    onChange={(e) => handleFilterChange('searchText', e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Results */}
      <div className="col-12">
        <div className="box box-default">
          <div className="box-header with-border">
            <h3 className="box-title">
              <i className="fas fa-list" style={{ marginRight: "8px" }}></i>
              Resultaten
              <span className="badge badge-primary" style={{ marginLeft: "10px" }}>
                {filteredLogs.length} van {logs.length}
              </span>
            </h3>
          </div>
          <div className="box-body">
            {error && (
              <div className="alert alert-danger">
                <strong>Fout:</strong> {error}
              </div>
            )}

            {filteredLogs.length === 0 ? (
              <div className="alert alert-info">
                <i className="fas fa-info-circle me-2"></i>
                {logs.length === 0
                  ? "Geen activiteiten gevonden voor deze boeking."
                  : "Geen activiteiten gevonden die voldoen aan de filteropties."}
              </div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead className="thead-light">
                    <tr>
                      <th style={{ width: "140px" }}>Datum & Tijd</th>
                      <th style={{ width: "180px" }}>Actie</th>
                      <th>Beschrijving</th>
                      <th style={{ width: "120px" }}>Door</th>
                      <th style={{ width: "100px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredLogs.map((log, index) => (
                      <tr key={log._id || index}>
                        <td>
                          <div style={{ fontSize: "13px" }}>
                            <div>{moment(log.created_at).format('DD-MM-YYYY')}</div>
                            <small className="text-muted">{moment(log.created_at).format('HH:mm:ss')}</small>
                          </div>
                        </td>
                        <td>
                          <div className="d-flex align-items-center">
                            <i className={`${getActionTypeIcon(log.action_type)} me-2`} style={{ fontSize: '1.2em' }}></i>
                            <span className="badge badge-secondary" style={{ fontSize: "11px" }}>
                              {getActionTypeLabel(log.action_type)}
                            </span>
                          </div>
                        </td>
                        <td>
                          <div>
                            {log.description}
                            {log.error_message && (
                              <div className="text-danger small mt-1">
                                <i className="fas fa-exclamation-triangle me-1"></i>
                                {log.error_message}
                              </div>
                            )}
                          </div>
                        </td>
                        <td>
                          <div style={{ fontSize: "12px" }}>
                            <div><strong>{log.performed_by?.user_name || 'Onbekend'}</strong></div>
                            <small className="text-muted">{getUserTypeLabel(log.performed_by?.user_type)}</small>
                          </div>
                        </td>
                        <td>
                          {log.success ? (
                            <span className="badge badge-success">
                              <i className="fas fa-check me-1"></i>
                              Succes
                            </span>
                          ) : (
                            <span className="badge badge-danger">
                              <i className="fas fa-times me-1"></i>
                              Mislukt
                            </span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

function mapStateToProps(state) {
  return {
    logs: state.gastenverbruik.activityLogs || [],
    loading: state.gastenverbruik.activityLogsLoading || false,
    error: state.gastenverbruik.activityLogsError,
    gastverbruik: state.gastenverbruik.gastverbruik
  };
}

export default connect(mapStateToProps, {
  fetchActivityLogs,
  fetchgastenverbruikone,
  message
})(ActivityLogPage);